import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
  Home, Inbox, Users, Building2, Briefcase, ListChecks, BarChart3,
  Settings as SettingsIcon, LogOut, AlertTriangle, UserCheck, Package,
  FileText, DoorOpen,
  Search, Bell, Sun, Moon, User as UserIcon, KeyRound, CreditCard, ExternalLink,
  TrendingUp,
} from "lucide-react";
import GlobalSearchPopover from "./GlobalSearchPopover";
import NotificationDropdown, { useNotificationCount } from "./NotificationDropdown";
import { ProfileEditModal, ChangePasswordModal } from "./ProfilePanels";
import { profileImageSrc } from "../lib/profileImage";
import TrialBanner from "./TrialBanner";
import { useBillingStatus } from "../lib/billing";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import ProductTour, { useProductTourController } from "./ProductTour";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: Home, testid: "nav-dashboard" },
  { to: "/moves", label: "Moves", icon: ListChecks, testid: "nav-moves" },
  { to: "/leads", label: "Lead Inbox", icon: Inbox, testid: "nav-leads" },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/companies", label: "Companies", icon: Building2, testid: "nav-companies" },
  { to: "/deals", label: "Acquisition", icon: Briefcase, testid: "nav-deals" },
  { to: "/clients", label: "Clients", icon: UserCheck, testid: "nav-clients" },
  { to: "/growth", label: "Growth", icon: TrendingUp, testid: "nav-growth" },
  { to: "/deliverables", label: "Deliverables", icon: Package, testid: "nav-deliverables" },
  { to: "/proposals", label: "Proposals", icon: FileText, testid: "nav-proposals" },
  { to: "/rooms", label: "Rooms", icon: DoorOpen, testid: "nav-rooms" },
];

const insightsNav = { to: "/insights", label: "Insights", icon: BarChart3, testid: "nav-insights" };
const settingsNav = { to: "/settings", label: "Settings", icon: SettingsIcon, testid: "nav-settings" };

const breadcrumbFor = (pathname) => {
  const seg = (pathname || "/").split("/").filter(Boolean)[0] || "dashboard";
  if (seg === "leads") return "Lead Inbox";
  if (seg === "dashboard") return "Dashboard";
  if (seg === "deals") return "Acquisition";
  if (seg === "growth") return "Growth";
  return seg.charAt(0).toUpperCase() + seg.slice(1);
};

export default function Layout() {
  const { user, tenant, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const [searchOpen, setSearchOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  const searchAnchorRef = useRef(null);
  const notifAnchorRef = useRef(null);
  const profileAnchorRef = useRef(null);

  const { count: notifCount, refresh: refreshNotifs } = useNotificationCount();
  const { billing } = useBillingStatus();
  const productTour = useProductTourController();
  const isAdmin = user?.role === "admin";
  const canManageSub = isAdmin && billing?.has_subscription && billing?.stripe_configured !== false;
  const [portalBusy, setPortalBusy] = useState(false);

  const openStripePortal = async () => {
    if (portalBusy) return;
    setPortalBusy(true);
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not open billing portal");
      setPortalBusy(false);
    }
  };

  // Close popovers on outside click
  useEffect(() => {
    const onDown = (e) => {
      if (searchOpen && searchAnchorRef.current && !searchAnchorRef.current.contains(e.target)) {
        setSearchOpen(false);
      }
      if (notifOpen && notifAnchorRef.current && !notifAnchorRef.current.contains(e.target)) {
        setNotifOpen(false);
      }
      if (profileOpen && profileAnchorRef.current && !profileAnchorRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [searchOpen, notifOpen, profileOpen]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setSearchOpen(false); setNotifOpen(false); setProfileOpen(false); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const exitImpersonation = () => {
    localStorage.removeItem("pragma_token");
    window.location.href = "/superadmin";
  };

  const initials = (user?.first_name?.[0] || "") + (user?.last_name?.[0] || "");
  const initialsFallback = (user?.name || "?").split(" ").map(p => p[0]).slice(0, 2).join("");
  const avatarText = (initials || initialsFallback || "?").toUpperCase();
  const avatarPhoto = user?.profile_image_url ? profileImageSrc(user.profile_image_url) : null;
  const tenantName = tenant?.name || "Pragma";
  const isImpersonating = !!user?.impersonated_by;
  const isLight = theme === "light";

  return (
    <div className="app-shell">
      {/* ─── SIDENAV RAIL (72px) ─── */}
      <aside className="sidenav-rail" data-testid="sidebar">
        <div className="sidenav-logo">
          <img src="/pragma-icon-v2.png" alt="Pragma" />
        </div>

        <div className="sidenav-items">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-tip={n.label}
              data-testid={n.testid}
              className={({ isActive }) => `nav-rail-item ${isActive ? "active" : ""}`}
            >
              <n.icon strokeWidth={1.8} />
            </NavLink>
          ))}

          <div className="nav-rail-divider" />

          <NavLink
            to={insightsNav.to}
            data-tip={insightsNav.label}
            data-testid={insightsNav.testid}
            className={({ isActive }) => `nav-rail-item ${isActive ? "active" : ""}`}
          >
            <insightsNav.icon strokeWidth={1.8} />
          </NavLink>
        </div>

        <div className="sidenav-bottom">
        </div>
      </aside>

      {/* ─── MAIN COLUMN ─── */}
      <div className="main-col">
        <span style={{ display: "none" }} data-testid="current-user-name">{user?.name}</span>
        <span style={{ display: "none" }} data-testid="sidebar-tenant-name">{tenantName}</span>

        {/* Glass topbar */}
        <header className="topbar-glass">
          <div className="topbar-left">
            <span className="topbar-company">{tenantName}</span>
            <div className="breadcrumb">
              <span className="breadcrumb-sep">/</span>
              <span className="breadcrumb-current" data-testid="breadcrumb-current">{breadcrumbFor(location.pathname)}</span>
            </div>
          </div>
          <div className="topbar-right">
            {/* Global search */}
            <div className="global-search-anchor" ref={searchAnchorRef}>
              <button
                className="icon-btn-glass"
                aria-label="Search"
                data-testid="topbar-search"
                onClick={() => setSearchOpen((s) => !s)}
              >
                <Search strokeWidth={2} />
              </button>
              {searchOpen && <GlobalSearchPopover onClose={() => setSearchOpen(false)} />}
            </div>

            {/* Theme toggle — sits between search and notifications */}
            <button
              type="button"
              onClick={() => setTheme(isLight ? "dark" : "light")}
              className="icon-btn-glass"
              data-testid="theme-toggle"
              aria-label={isLight ? "Switch to dark theme" : "Switch to light theme"}
              title={isLight ? "Switch to dark" : "Switch to light"}
            >
              {isLight ? <Moon strokeWidth={2} /> : <Sun strokeWidth={2} />}
            </button>

            <div className="notifications-anchor" ref={notifAnchorRef}>
              <button
                className="icon-btn-glass"
                aria-label="Notifications"
                data-testid="topbar-notifications"
                onClick={() => setNotifOpen((s) => !s)}
                style={{ position: "relative" }}
              >
                <Bell strokeWidth={2} />
                {notifCount > 0 && (
                  <span className="notification-badge" data-testid="notification-badge">
                    {notifCount > 99 ? "99+" : notifCount}
                  </span>
                )}
              </button>
              {notifOpen && (
                <NotificationDropdown
                  onClose={() => setNotifOpen(false)}
                  onChange={refreshNotifs}
                />
              )}
            </div>
            {/* Profile menu (avatar in top-right replacing the old More button) */}
            <div className="profile-menu-anchor" ref={profileAnchorRef}>
              <button
                type="button"
                className="topbar-avatar-btn"
                data-testid="current-user-avatar"
                aria-label="Open profile menu"
                onClick={() => setProfileOpen((s) => !s)}
                style={avatarPhoto ? { padding: 0, overflow: "hidden" } : undefined}
              >
                {avatarPhoto ? (
                  <img
                    src={avatarPhoto}
                    alt="Profile"
                    style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                    data-testid="current-user-avatar-img"
                  />
                ) : avatarText}
              </button>
              {profileOpen && (
                <div className="profile-menu-pop" data-testid="profile-menu-popover" onMouseDown={(e) => e.stopPropagation()}>
                  <div className="profile-menu-header">
                    <div className="profile-menu-name" data-testid="profile-menu-name">{user?.name}</div>
                    <div className="profile-menu-email" data-testid="profile-menu-email">{user?.email}</div>
                  </div>
                  <button
                    type="button"
                    className="profile-menu-item"
                    data-testid="profile-menu-edit"
                    onClick={() => { setProfileOpen(false); setShowProfileEdit(true); }}
                  >
                    <UserIcon /> Edit profile
                  </button>
                  <button
                    type="button"
                    className="profile-menu-item"
                    data-testid="profile-menu-password"
                    onClick={() => { setProfileOpen(false); setShowChangePassword(true); }}
                  >
                    <KeyRound /> Change password
                  </button>
                  <button
                    type="button"
                    className="profile-menu-item"
                    data-testid="profile-menu-billing"
                    onClick={() => { setProfileOpen(false); navigate("/billing"); }}
                  >
                    <CreditCard /> Billing
                  </button>
                  {canManageSub && (
                    <button
                      type="button"
                      className="profile-menu-item"
                      data-testid="profile-menu-manage-subscription"
                      onClick={() => { setProfileOpen(false); openStripePortal(); }}
                      disabled={portalBusy}
                    >
                      <ExternalLink /> {portalBusy ? "Opening…" : "Manage subscription"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="profile-menu-item"
                    data-testid="profile-menu-settings"
                    onClick={() => { setProfileOpen(false); navigate("/settings"); }}
                  >
                    <SettingsIcon /> Company settings
                  </button>
                  <div className="profile-menu-divider" />
                  <button
                    type="button"
                    className="profile-menu-item"
                    data-testid="profile-menu-logout"
                    onClick={() => { setProfileOpen(false); doLogout(); }}
                  >
                    <LogOut /> Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {isImpersonating && (
          <div className="impersonation-banner-glass" data-testid="impersonation-banner">
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} style={{ color: "var(--pg-orange)" }} />
              <span>Impersonating <strong>{tenantName}</strong> as <strong>{user?.name}</strong></span>
            </div>
            <button onClick={exitImpersonation} className="btn-pill-primary" style={{ height: 30, padding: "0 14px" }} data-testid="exit-impersonation">
              Exit impersonation
            </button>
          </div>
        )}

        <TrialBanner />

        <main className="page-scroll">
          <Outlet />
        </main>
      </div>

      {showProfileEdit && (
        <ProfileEditModal onClose={() => setShowProfileEdit(false)} />
      )}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}

      <ProductTour open={productTour.open} onClose={productTour.close} />
    </div>
  );
}
